import { Store } from '@tanstack/react-store';
import { nanoid } from 'nanoid';
import { get, set } from 'idb-keyval';
import { Campaign, VideoItem } from './types';

interface VideoCompState {
  campaigns: Campaign[];
  selectedCampaignId: string;
}

export const DEFAULT_CAMPAIGN: Campaign = {
  id: 'default_1',
  name: 'Task 1',
  context: 'General quality control check for video content.',
  doText: 'Video must be clear and not blurry',
  dontText: 'No watermarks',
  language: 'English',
  videos: []
};

const STORAGE_KEY = 'videocomp_store_v1';

export const store = new Store<VideoCompState>({
  campaigns: [DEFAULT_CAMPAIGN],
  selectedCampaignId: DEFAULT_CAMPAIGN.id
});

export const initPersistence = async () => {
  try {
    const savedState = await get<VideoCompState>(STORAGE_KEY);
    if (savedState) {
      store.setState(() => savedState);
    }
  } catch (err) {
    console.error('Failed to load state from IDB:', err);
  }

  store.subscribe(() => {
    const stateToSave = {
      ...store.state,
      campaigns: store.state.campaigns.map((c) => ({
        ...c,
        videos: c.videos.map((v) => {
          // We want to save everything EXCEPT the raw 'file' objects if any exist.
          const { file: _file, ...rest } = v;
          return rest;
        })
      }))
    };
    set(STORAGE_KEY, stateToSave).catch((err) => console.error('Failed to save state to IDB:', err));
  });
};

export const actions = {
  createCampaign: () => {
    store.setState((state) => {
      if (state.campaigns.length >= 10) return state;
      const newId = nanoid(9);
      return {
        ...state,
        campaigns: [
          ...state.campaigns,
          {
            ...DEFAULT_CAMPAIGN,
            id: newId,
            name: `Task ${state.campaigns.length + 1}`,
            videos: []
          }
        ],
        selectedCampaignId: newId
      };
    });
  },

  deleteCampaign: (id: string) => {
    store.setState((state) => {
      if (state.campaigns.length <= 1) return state;
      const newCampaigns = state.campaigns.filter((c) => c.id !== id);
      const newSelectedId = state.selectedCampaignId === id ? newCampaigns[0].id : state.selectedCampaignId;
      return {
        ...state,
        campaigns: newCampaigns,
        selectedCampaignId: newSelectedId
      };
    });
  },

  setSelectedCampaignId: (id: string) => {
    store.setState((state) => ({ ...state, selectedCampaignId: id }));
  },

  updateCampaign: (field: keyof Campaign, value: string) => {
    store.setState((state) => ({
      ...state,
      campaigns: state.campaigns.map((c) => (c.id === state.selectedCampaignId ? { ...c, [field]: value } : c))
    }));
  },

  addVideosToCampaign: (newVideos: VideoItem[]) => {
    store.setState((state) => ({
      ...state,
      campaigns: state.campaigns.map((c) =>
        c.id === state.selectedCampaignId ? { ...c, videos: [...c.videos, ...newVideos] } : c
      )
    }));
  },

  setVideosToCampaign: (videos: VideoItem[]) => {
    store.setState((state) => ({
      ...state,
      campaigns: state.campaigns.map((c) => (c.id === state.selectedCampaignId ? { ...c, videos } : c))
    }));
  },

  removeVideoFromCampaign: (videoId: string) => {
    store.setState((state) => ({
      ...state,
      campaigns: state.campaigns.map((c) =>
        c.id === state.selectedCampaignId ? { ...c, videos: c.videos.filter((v) => v.id !== videoId) } : c
      )
    }));
  },

  clearHistory: () => {
    store.setState((state) => ({
      ...state,
      campaigns: state.campaigns.map((c) =>
        c.id === state.selectedCampaignId ? { ...c, videos: c.videos.filter((v) => v.file !== undefined) } : c
      )
    }));
  },

  updateVideoStatus: (videoId: string, updates: Partial<VideoItem>) => {
    store.setState((state) => ({
      ...state,
      campaigns: state.campaigns.map((c) =>
        c.id === state.selectedCampaignId
          ? {
              ...c,
              videos: c.videos.map((v) => (v.id === videoId ? { ...v, ...updates } : v))
            }
          : c
      )
    }));
  },

  resetStore: () => {
    store.setState(() => ({
      campaigns: [DEFAULT_CAMPAIGN],
      selectedCampaignId: DEFAULT_CAMPAIGN.id
    }));
    set(STORAGE_KEY, { campaigns: [DEFAULT_CAMPAIGN], selectedCampaignId: DEFAULT_CAMPAIGN.id });
  }
};
